import { CourseProgressStrip } from '@components/FeaturedProduct/CourseProgressStrip'
import { ExtraContentsSection } from '@components/FeaturedProduct/ExtraContentsSection'
import { ModulesCardsSection } from '@components/FeaturedProduct/ModulesCardsSection'
import { HeroCarousel } from '@components/HeroCarousel'
import { Banner } from '@components/Home/Banner'
import { ContinueLessonsSection } from '@components/Home/Continue/ContinueLessonsSection'
import { CustomBanners } from '@components/Home/CustomBanners'
import { CustomProductList } from '@components/Home/CustomProductList'
import { HomeLoadingSkeleton } from '@components/Home/HomeLoadingSkeleton'
import {
  CourseModuleSection,
  CourseModulesWrapper,
} from '@components/enrollments/courses/CourseModuleSection'
import { BANNER_HEIGHT, BANNER_MAX_HEIGHT } from '@constants/homeBanners'
import { ContinueLessonsProvider } from '@contexts/ContinueLessonsContext'
import { CourseContentsProvider } from '@contexts/CourseContentsContext'
import { CourseContextsResetProvider } from '@contexts/CourseContextsReset'
import { CourseProvider } from '@contexts/CourseContext'
import { CourseRouteParamsProvider } from '@contexts/CourseRouteParamsContext'
import { EnrollmentProvider } from '@contexts/EnrollmentContext'
import { ExtraContentsProvider } from '@contexts/ExtraContentsContext'
import { LessonExtraContentsProvider } from '@contexts/LessonExtraContentsContext'
import { ModulesProvider } from '@contexts/ModulesContext'
import { IFeaturedProduct } from '@dtos/MembersConfig'
import { IContinueLessons } from '@dtos/User'
import { useAdminPreview } from '@hooks/useAdminPreview'
import { useCurrentLesson } from '@hooks/useCurrentLesson'
import { useEnrollment } from '@hooks/useEnrollment'
import { useMediaSize } from '@hooks/useMediaSize'
import { useMembersConfig } from '@hooks/useMembersConfig'
import { usePushValueMoment } from '@hooks/usePushValueMoment'
import { useModules } from '@hooks/useModules'
import { AppLayout } from '@layouts/AppLayout'
import { useApolloClient } from '@apollo/client'
import { useGetFeaturedContinueLessons } from '@services/api/graphql/featuredContinueLessons'
import { filterAvailableModules } from '@utils/courseModules'
import {
  buildContinueModulesQuery,
  FEATURED_CONTINUE_LIMIT,
  toFeaturedContinueLessons,
  withContinueModuleIds,
} from '@utils/featuredContinueLessons'
import { useFocusEffect } from 'expo-router'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { getTokens, isWeb, useMedia, useWindowDimensions, View } from 'tamagui'

interface IFeaturedProductHomeProps {
  featuredProduct: IFeaturedProduct
}

function FeaturedProductHomeContent({
  featuredProduct,
}: IFeaturedProductHomeProps) {
  const [continueLessons, setContinueLessons] = useState<
    IContinueLessons[] | null
  >(null)
  const hasLoadedOnce = useRef(false)
  const { setCurrentLesson } = useCurrentLesson()
  const { isPreview } = useAdminPreview()
  const { data: membersConfigData } = useMembersConfig()
  const { enrollment, enrollmentIsLoading } = useEnrollment()
  usePushValueMoment(!isPreview && !!enrollment)
  const {
    modules,
    isLoading: modulesLoading,
    onLoadMoreSubmodules,
    hasMoreSubmodules,
  } = useModules()
  const { getFeaturedContinueLessons, loading: continueLessonsLoading } =
    useGetFeaturedContinueLessons()
  const apolloClient = useApolloClient()
  const { height } = useWindowDimensions()
  const size = useMediaSize()
  const media = useMedia()

  const bannerHeight = height * (BANNER_HEIGHT[size] || BANNER_HEIGHT.xs)
  const bannerMaxHeight = BANNER_MAX_HEIGHT[size] || BANNER_MAX_HEIGHT.xs
  const filteredActiveBanners = useMemo(
    () =>
      membersConfigData?.header?.banners.filter((banner) => banner.active) ||
      [],
    [membersConfigData],
  )
  const showBanners = filteredActiveBanners.length > 0

  const sectionsGap = useMemo(() => {
    const sizes = getTokens().size
    if (media.gtXxl) return sizes['$spacing-xxl'].val
    if (media.gtLg) return sizes['$spacing-xsl'].val
    if (media.gtMd) return sizes['$spacing-xl'].val
    return sizes['$spacing-lg'].val
  }, [media.gtXxl, media.gtLg, media.gtMd])

  const firstModule = useMemo(
    () => filterAvailableModules(modules?.nodes ?? [])[0],
    [modules],
  )

  // Continue só do produto principal (FR-016), com até 5 aulas: o
  // `lastViewedContents` da home traz uma aula por curso. O preview não tem
  // progresso, como na home padrão.
  const loadContinueLessons = useCallback(async () => {
    if (isPreview) {
      setContinueLessons([])
      return
    }
    if (!featuredProduct.enrollmentId) {
      setContinueLessons([])
      return
    }
    try {
      const { data } = await getFeaturedContinueLessons({
        variables: {
          id: String(featuredProduct.enrollmentId),
          courseId: String(featuredProduct.courseId),
          limit: FEATURED_CONTINUE_LIMIT,
        },
      })
      const courseId = String(featuredProduct.courseId)
      const items = toFeaturedContinueLessons(data?.enrollment, courseId)
      const modulesQuery = buildContinueModulesQuery(
        items,
        courseId,
        String(featuredProduct.enrollmentId),
      )
      if (!modulesQuery) {
        setContinueLessons(items)
        return
      }
      // Sem o módulo, o card ainda abre a aula; só a lista do curso não abre
      // no módulo dela.
      const modules = await apolloClient
        .query({ query: modulesQuery })
        .catch(() => null)
      setContinueLessons(withContinueModuleIds(items, modules?.data))
    } catch {
      setContinueLessons([])
    }
  }, [
    apolloClient,
    getFeaturedContinueLessons,
    isPreview,
    featuredProduct.enrollmentId,
    featuredProduct.courseId,
  ])

  useFocusEffect(
    useCallback(() => {
      loadContinueLessons()
    }, [loadContinueLessons]),
  )

  useEffect(() => {
    setCurrentLesson(null)
  }, [setCurrentLesson])

  const isLoading =
    continueLessons === null || enrollmentIsLoading || modulesLoading
  // Como na home padrão: o esqueleto só aparece na primeira carga, não a cada
  // refetch de fundo.
  if (!isLoading) hasLoadedOnce.current = true
  if (!hasLoadedOnce.current) return <HomeLoadingSkeleton />

  const hasEnrollment = !!enrollment
  // "Layout dos produtos" do painel: sem nome do produto, os cards de aula do
  // primeiro módulo e os de módulo também saem sem nome, como em "Meus produtos".
  const showProductName = membersConfigData?.general?.showProductName ?? true
  const showContinue = !!continueLessons && continueLessons.length > 0
  // Sem banner, o topo fica sob o header, como na home padrão.
  const firstSectionMarginTop = showBanners ? 0 : 122
  // No desktop web o progresso vai na linha do título do Continue; no mobile e
  // no app fica numa faixa própria entre o banner e o Continue.
  const progressBesideContinue = isWeb && media.gtMd && showContinue

  return (
    <AppLayout backRoute={{ label: '', shouldDisplayLogo: true }}>
      <View gap={sectionsGap} flexGrow={1} testID="featured-product-home">
        {showBanners && (
          <View testID="hero-carousel">
            <HeroCarousel
              height={bannerHeight}
              maxHeight={bannerMaxHeight}
              numberOfSlides={filteredActiveBanners.length}
              displayControls={false}
              autoPlay={true}
              stopOnHover={false}
              autoPlayInterval={10000}
              indicatorsStyles={{
                position: 'absolute',
                width: '100%',
                bottom: 28,
                zIndex: 1000,
                flex: 1,
              }}
            >
              {filteredActiveBanners.map((item, index) => (
                <Banner key={index} bannerData={item} height={bannerHeight} />
              ))}
            </HeroCarousel>
          </View>
        )}
        {!progressBesideContinue && (
          <View marginTop={firstSectionMarginTop}>
            <CourseProgressStrip />
          </View>
        )}
        {showContinue && (
          <View marginTop={progressBesideContinue ? firstSectionMarginTop : 0}>
            <ContinueLessonsProvider
              continueLessons={continueLessons}
              isLoading={continueLessonsLoading}
            >
              <ContinueLessonsSection
                showProductName={showProductName}
                cardTitle="lesson"
                titleAccessory={
                  progressBesideContinue ? <CourseProgressStrip inline /> : null
                }
              />
            </ContinueLessonsProvider>
          </View>
        )}
        {hasEnrollment && featuredProduct.showFirstModule && firstModule && (
          <CourseModulesWrapper id="featured-first-module" flush>
            <CourseModuleSection
              flush
              module={firstModule}
              index={0}
              canLoadMoreSubmodules={hasMoreSubmodules?.(firstModule) ?? false}
              onLoadMoreSubmodules={
                onLoadMoreSubmodules
                  ? () => onLoadMoreSubmodules(firstModule.id)
                  : undefined
              }
              showLessonTitle={showProductName}
            />
          </CourseModulesWrapper>
        )}
        {featuredProduct.showModules && (
          <ModulesCardsSection
            title={featuredProduct.modulesTitle}
            featuredProduct={featuredProduct}
            showModuleName={showProductName}
          />
        )}
        {featuredProduct.showExtraContents && (
          <ExtraContentsSection title={featuredProduct.extraContentsTitle} />
        )}
        <CustomProductList />
        <CustomBanners />
      </View>
    </AppLayout>
  )
}

/**
 * Home focada no produto principal: a pilha de providers da tela do curso
 * (`CourseScreen`) montada em `/`, com os ids vindos da configuração em vez da
 * URL (`CourseRouteParamsProvider`).
 */
export function FeaturedProductHome({
  featuredProduct,
}: IFeaturedProductHomeProps) {
  const { isPreview } = useAdminPreview()

  // No preview a matrícula é a sintética do `EnrollmentProvider`, e os links
  // saem sob /admin_view (`coursePaths`).
  return (
    <CourseRouteParamsProvider
      courseId={featuredProduct.courseId}
      enrollmentId={isPreview ? null : featuredProduct.enrollmentId}
    >
      <CourseProvider>
        <CourseContentsProvider>
          <ModulesProvider>
            <LessonExtraContentsProvider>
              <ExtraContentsProvider>
                <EnrollmentProvider>
                  <CourseContextsResetProvider>
                    <FeaturedProductHomeContent
                      featuredProduct={featuredProduct}
                    />
                  </CourseContextsResetProvider>
                </EnrollmentProvider>
              </ExtraContentsProvider>
            </LessonExtraContentsProvider>
          </ModulesProvider>
        </CourseContentsProvider>
      </CourseProvider>
    </CourseRouteParamsProvider>
  )
}
